import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  TextField,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Icon,
  Spinner,
  Badge,
  Box,
  Divider,
  Tooltip,
  ButtonGroup,
} from '@shopify/polaris';
import { apiFetch } from '../hooks/useApi';
import {
  ChatIcon,
  SendIcon,
  DeleteIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  RefreshIcon,
} from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';
import { useShop } from '../context/ShopContext';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  feedback?: 'helpful' | 'not_helpful';
}

interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
  messageCount: number;
}

interface ChatResponse {
  message: string;
  conversationId: string;
  messageId: string;
  language: string;
  suggestedQuestions: string[];
  debug?: {
    retrievedSources: string[];
    contextTokens: number;
    processingTimeMs: number;
    modelUsed: string;
  };
}

export default function ChatWidget() {
  const { shop } = useShop();
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    if (shop) {
      loadConversations();
    }
  }, [shop]);

  // Initial welcome message
  useEffect(() => {
    if (messages.length === 0 && !conversationId) {
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: t('chat.welcomeText'),
        timestamp: new Date().toISOString(),
      }]);
      setSuggestedQuestions([
        t('chat.suggestQ1'),
        t('chat.suggestQ2'),
        t('chat.suggestQ3'),
      ]);
    }
  }, [t]);

  const loadConversations = async () => {
    try {
      const response = await apiFetch(`/api/chat/conversations`);
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  };

  const loadConversation = async (convId: string) => {
    try {
      const response = await apiFetch(`/api/chat/conversations/${convId}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setConversationId(convId);
        setShowHistory(false);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
    }
  };

  const sendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || !shop) return;

    setError(null);
    setInputValue('');
    setSuggestedQuestions([]);

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await apiFetch(`/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          conversationId,
          language: i18n.language?.split('-')[0] || 'en',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data: ChatResponse = await response.json();

      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: data.messageId,
        role: 'assistant',
        content: data.message,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      setConversationId(data.conversationId);
      setSuggestedQuestions(data.suggestedQuestions);

      // Refresh conversations list
      loadConversations();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('chat.errorOccurred'));
      // Remove the user message on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, shop, conversationId]);

  const submitFeedback = async (messageId: string, feedback: 'helpful' | 'not_helpful') => {
    if (!conversationId || !shop) return;

    try {
      await apiFetch(`/api/chat/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversationId,
          messageId,
          feedback,
        }),
      });

      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, feedback } : m
      ));
    } catch (err) {
      console.error('Error submitting feedback:', err);
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: t('chat.welcomeText'),
      timestamp: new Date().toISOString(),
    }]);
    setSuggestedQuestions([
      t('chat.suggestQ1'),
      t('chat.suggestQ2'),
      t('chat.suggestQ3'),
    ]);
    setShowHistory(false);
  };

  const deleteConversation = async (convId: string) => {
    if (!shop) return;

    try {
      await apiFetch(`/api/chat/conversations/${convId}`, {
        method: 'DELETE',
      });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        startNewConversation();
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Icon source={ChatIcon} tone="primary" />
            <Text as="h2" variant="headingMd">{t('chat.pageTitle')}</Text>
            <Badge tone="success">{t('chat.betaBadge')}</Badge>
          </InlineStack>
          <ButtonGroup>
            <Button
              icon={RefreshIcon}
              onClick={startNewConversation}
              accessibilityLabel={t('chat.newConversation')}
            />
            <Button
              onClick={() => setShowHistory(!showHistory)}
              pressed={showHistory}
            >
              {t('chat.history')}
            </Button>
          </ButtonGroup>
        </InlineStack>

        <Divider />

        {/* History Panel */}
        {showHistory && (
          <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">{t('chat.previousConversations')}</Text>
              {conversations.length === 0 ? (
                <Text as="p" tone="subdued">{t('chat.noConversations')}</Text>
              ) : (
                conversations.map(conv => (
                  <InlineStack key={conv.id} align="space-between" blockAlign="center">
                    <Button
                      variant="plain"
                      onClick={() => loadConversation(conv.id)}
                      textAlign="start"
                    >
                      {conv.title}
                    </Button>
                    <Button
                      icon={DeleteIcon}
                      variant="plain"
                      tone="critical"
                      onClick={() => deleteConversation(conv.id)}
                      accessibilityLabel={t('chat.deleteConversation')}
                    />
                  </InlineStack>
                ))
              )}
            </BlockStack>
          </Box>
        )}

        {/* Messages */}
        <div
          style={{
            minHeight: '300px',
            maxHeight: '500px',
            overflowY: 'auto',
            padding: '8px'
          }}
        >
          <BlockStack gap="400">
            {messages.map((message) => (
              <Box
                key={message.id}
                padding="300"
                background={message.role === 'user' ? 'bg-surface-secondary' : 'bg-surface'}
                borderRadius="200"
                borderWidth="025"
                borderColor="border"
              >
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Badge tone={message.role === 'user' ? 'info' : 'success'}>
                      {message.role === 'user' ? t('common.you') : t('chat.title')}
                    </Badge>
                    <Text as="span" variant="bodySm" tone="subdued">
                      {new Date(message.timestamp).toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Text>
                  </InlineStack>
                  
                  <div className="chat-message-content">
                    <Text as="p" variant="bodyMd">
                      {message.content.split('\n').map((line, i) => (
                        <span key={i}>
                          {line}
                          {i < message.content.split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </Text>
                  </div>

                  {/* Feedback buttons for assistant messages */}
                  {message.role === 'assistant' && message.id !== 'welcome' && (
                    <InlineStack gap="200">
                      <Tooltip content={t('chat.tooltipHelpful')}>
                        <Button
                          icon={ThumbsUpIcon}
                          variant="plain"
                          tone={message.feedback === 'helpful' ? 'success' : undefined}
                          onClick={() => submitFeedback(message.id, 'helpful')}
                          accessibilityLabel={t('chat.markHelpful')}
                        />
                      </Tooltip>
                      <Tooltip content={t('chat.tooltipNotHelpful')}>
                        <Button
                          icon={ThumbsDownIcon}
                          variant="plain"
                          tone={message.feedback === 'not_helpful' ? 'critical' : undefined}
                          onClick={() => submitFeedback(message.id, 'not_helpful')}
                          accessibilityLabel={t('chat.markNotHelpful')}
                        />
                      </Tooltip>
                    </InlineStack>
                  )}
                </BlockStack>
              </Box>
            ))}
            
            {isLoading && (
              <Box padding="300">
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text as="span" tone="subdued">{t('chat.thinking')}</Text>
                </InlineStack>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </BlockStack>
        </div>

        {/* Suggested Questions */}
        {suggestedQuestions.length > 0 && !isLoading && (
          <Box padding="200">
            <BlockStack gap="200">
              <Text as="span" variant="bodySm" tone="subdued">{t('chat.suggestions')}</Text>
              <InlineStack gap="200" wrap>
                {suggestedQuestions.map((q, i) => (
                  <Button
                    key={i}
                    size="slim"
                    onClick={() => sendMessage(q)}
                  >
                    {q}
                  </Button>
                ))}
              </InlineStack>
            </BlockStack>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box padding="200" background="bg-surface-critical">
            <Text as="p" tone="critical">{error}</Text>
          </Box>
        )}

        {/* Input */}
        <InlineStack gap="200" blockAlign="end">
          <div 
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          >
            <TextField
              label=""
              labelHidden
              value={inputValue}
              onChange={setInputValue}
              placeholder={t('chat.inputPlaceholder')}
              multiline={2}
              autoComplete="off"
              disabled={isLoading}
            />
          </div>
          <Button
            variant="primary"
            icon={SendIcon}
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || isLoading}
            accessibilityLabel={t('chat.send')}
          >
            {t('chat.send')}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
